import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Alert,
  FlatList,
  I18nManager,
  Modal,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { alarmRulesApi } from '../services/alarmRulesApi';
import { getErrorMessage, handleError, parseError } from '../utils/errorHandler';
import { canManageDevices } from '../utils/permissions';
import { useHeaderHeight } from '@react-navigation/elements';

interface CustomTemplate {
  id: string;
  name: string;
  nameAr: string;
  description: string;
  descriptionAr: string;
  category: string;
  isActive: boolean;
  rules: Array<{
    type: string;
    severity: 'info' | 'warning' | 'critical';
    thresholdLow?: number;
    thresholdHigh?: number;
    hysteresis: number;
    debounceSec: number;
    cooldownSec: number;
  }>;
  createdAt: string;
  updatedAt: string;
}

interface RuleForm {
  type: 'temperature' | 'humidity' | 'battery';
  severity: 'info' | 'warning' | 'critical';
  thresholdLow: string;
  thresholdHigh: string;
  hysteresis: string;
  debounceSec: string;
  cooldownSec: string;
}

export default function CustomAlarmTemplatesScreen({ navigation }: any) {
  // Header is transparent (see stack navigator) — push content below it
  const headerHeight = useHeaderHeight();
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const { user } = useAuth();
  const userCanManageDevices = canManageDevices(user?.roles || []);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [templates, setTemplates] = useState<CustomTemplate[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const isPresentingRef = useRef(false); // P0: throttle guard — prevent dual-Modal crash on iOS 17
  const [selectedTemplate, setSelectedTemplate] = useState<CustomTemplate | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionAr, setDescriptionAr] = useState('');
  const [category, setCategory] = useState<'pharmaceutical' | 'medical' | 'laboratory'>('medical');
  const [isActive, setIsActive] = useState(true);
  const [rules, setRules] = useState<RuleForm[]>([]);

  const isArabic = i18n.language === 'ar';
  const isRTL = I18nManager.isRTL || isArabic;

  useEffect(() => {
    navigation.setOptions({
      title: isArabic ? 'قوالب التنبيهات المخصصة' : 'Custom Alarm Templates',
      headerTintColor: '#ffffff',
      headerRight: () =>
        userCanManageDevices ? (
          <TouchableOpacity
            style={{ marginRight: isRTL ? 0 : 16, marginLeft: isRTL ? 16 : 0 }}
            onPress={openCreateModal}
          >
            <Ionicons name="add-circle-outline" size={28} color="#FFFFFF" />
          </TouchableOpacity>
        ) : null,
    });
    loadTemplates();
  }, [isArabic, theme]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const data = await alarmRulesApi.getCustomTemplates();
      setTemplates(data as CustomTemplate[]);
    } catch (error) {
      setLoadError(true);
      handleError(error, 'CustomTemplates.loadTemplates', {
        showAlert: false,
        language: isArabic ? 'ar' : 'en',
      });
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    if (isPresentingRef.current) return; // P0: throttle — RNSScreen.mm:1968
    isPresentingRef.current = true;
    setShowEditModal(false); // guard: prevent dual-present
    resetForm();
    setShowCreateModal(true);
    setTimeout(() => {
      isPresentingRef.current = false;
    }, 600);
  };

  const openEditModal = (template: CustomTemplate) => {
    if (isPresentingRef.current) return; // P0: throttle — RNSScreen.mm:1968
    isPresentingRef.current = true;
    setSelectedTemplate(template);
    setName(template.name);
    setNameAr(template.nameAr);
    setDescription(template.description);
    setDescriptionAr(template.descriptionAr);
    setCategory(template.category as any);
    setIsActive(template.isActive);
    setShowCreateModal(false); // guard: prevent dual-present
    setRules(
      template.rules.map((r) => ({
        type: r.type as any,
        severity: r.severity,
        thresholdLow: r.thresholdLow?.toString() || '',
        thresholdHigh: r.thresholdHigh?.toString() || '',
        hysteresis: r.hysteresis.toString(),
        debounceSec: r.debounceSec.toString(),
        cooldownSec: r.cooldownSec.toString(),
      })),
    );
    setShowEditModal(true);
    setTimeout(() => {
      isPresentingRef.current = false;
    }, 600);
  };

  const resetForm = () => {
    setName('');
    setNameAr('');
    setDescription('');
    setDescriptionAr('');
    setCategory('medical');
    setIsActive(true);
    setRules([]);
    setSelectedTemplate(null);
  };

  const addRule = () => {
    setRules([
      ...rules,
      {
        type: 'temperature',
        severity: 'warning',
        thresholdLow: '',
        thresholdHigh: '',
        hysteresis: '0.5',
        debounceSec: '60',
        cooldownSec: '300',
      },
    ]);
  };

  const removeRule = (index: number) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const updateRule = (index: number, field: keyof RuleForm, value: any) => {
    const newRules = [...rules];
    newRules[index] = { ...newRules[index], [field]: value };
    setRules(newRules);
  };

  const handleSave = async () => {
    if (!name.trim() || !nameAr.trim()) {
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic
          ? 'الرجاء إدخال اسم القالب بالعربية والإنجليزية'
          : 'Please enter template name in both languages',
      );
      return;
    }

    if (rules.length === 0) {
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'الرجاء إضافة قاعدة واحدة على الأقل' : 'Please add at least one rule',
      );
      return;
    }

    try {
      setSaving(true);
      const templateData = {
        name,
        nameAr,
        description,
        descriptionAr,
        category,
        isActive,
        rules: rules.map((r) => ({
          type: r.type,
          severity: r.severity,
          thresholdLow: r.thresholdLow ? parseFloat(r.thresholdLow) : undefined,
          thresholdHigh: r.thresholdHigh ? parseFloat(r.thresholdHigh) : undefined,
          hysteresis: parseFloat(r.hysteresis),
          debounceSec: parseInt(r.debounceSec),
          cooldownSec: parseInt(r.cooldownSec),
        })),
      };

      if (selectedTemplate) {
        await alarmRulesApi.updateCustomTemplate(selectedTemplate.id, templateData);
      } else {
        await alarmRulesApi.createCustomTemplate(templateData);
      }

      Alert.alert(
        isArabic ? 'نجح' : 'Success',
        isArabic ? 'تم حفظ القالب بنجاح' : 'Template saved successfully',
      );

      setShowCreateModal(false);
      setShowEditModal(false);
      loadTemplates();
    } catch (error) {
      const appError = parseError(error);
      Alert.alert(isArabic ? 'خطأ' : 'Error', getErrorMessage(appError, isArabic ? 'ar' : 'en'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (template: CustomTemplate) => {
    Alert.alert(
      isArabic ? 'تأكيد الحذف' : 'Confirm Delete',
      isArabic
        ? `هل أنت متأكد من حذف القالب "${template.nameAr}"؟`
        : `Are you sure you want to delete "${template.name}"?`,
      [
        { text: isArabic ? 'إلغاء' : 'Cancel', style: 'cancel' },
        {
          text: isArabic ? 'حذف' : 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await alarmRulesApi.deleteCustomTemplate(template.id);
              Alert.alert(
                isArabic ? 'نجح' : 'Success',
                isArabic ? 'تم حذف القالب' : 'Template deleted',
              );
              loadTemplates();
            } catch (error) {
              const appError = parseError(error);
              Alert.alert(
                isArabic ? 'خطأ' : 'Error',
                getErrorMessage(appError, isArabic ? 'ar' : 'en'),
              );
            }
          },
        },
      ],
    );
  };

  const renderTemplateItem = ({ item }: { item: CustomTemplate }) => (
    <View
      style={[
        styles.templateCard,
        { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
      ]}
    >
      <View style={[styles.templateHeader, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
        <View style={{ flex: 1 }}>
          <Text
            style={[
              styles.templateName,
              { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
            ]}
          >
            {isArabic ? item.nameAr : item.name}
          </Text>
          <Text
            style={[
              styles.templateDescription,
              { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
            ]}
          >
            {isArabic ? item.descriptionAr : item.description}
          </Text>
          <View style={[styles.templateMeta, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
            <View style={[styles.categoryBadge, { backgroundColor: theme.colors.info + '20' }]}>
              <Text style={[styles.categoryText, { color: theme.colors.info }]}>
                {isArabic
                  ? item.category === 'pharmaceutical'
                    ? 'صيدلانية'
                    : item.category === 'medical'
                    ? 'طبية'
                    : 'مختبرية'
                  : item.category}
              </Text>
            </View>
            <Text style={[styles.rulesCount, { color: theme.colors.textSecondary }]}>
              {item.rules.length} {isArabic ? 'قاعدة' : 'rules'}
            </Text>
          </View>
        </View>
        <View style={[styles.templateActions, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
          {userCanManageDevices && (
            <>
              <TouchableOpacity
                style={[styles.actionButton, { backgroundColor: theme.colors.primary + '15' }]}
                onPress={() => openEditModal(item)}
              >
                <Ionicons name="create-outline" size={20} color={theme.colors.primary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.actionButton, { backgroundColor: theme.colors.error + '15' }]}
                onPress={() => handleDelete(item)}
              >
                <Ionicons name="trash-outline" size={20} color={theme.colors.error} />
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>
    </View>
  );

  const renderRuleForm = (rule: RuleForm, index: number) => (
    <View
      key={index}
      style={[
        styles.ruleCard,
        { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
      ]}
    >
      <View style={styles.ruleHeader}>
        <Text style={[styles.ruleTitle, { color: theme.colors.text }]}>
          {isArabic ? 'قاعدة' : 'Rule'} {index + 1}
        </Text>
        <TouchableOpacity onPress={() => removeRule(index)}>
          <Ionicons name="close-circle" size={24} color={theme.colors.error} />
        </TouchableOpacity>
      </View>

      {/* Type */}
      <View style={styles.formField}>
        <Text style={[styles.label, { color: theme.colors.text }]}>
          {isArabic ? 'النوع' : 'Type'}
        </Text>
        <View style={styles.segmentControl}>
          {(['temperature', 'humidity', 'battery'] as const).map((type) => (
            <TouchableOpacity
              key={type}
              style={[
                styles.segment,
                rule.type === type && { backgroundColor: theme.colors.primary },
                { borderColor: theme.colors.border },
              ]}
              onPress={() => updateRule(index, 'type', type)}
            >
              <Text
                style={[
                  styles.segmentText,
                  { color: rule.type === type ? '#fff' : theme.colors.text },
                ]}
              >
                {isArabic
                  ? type === 'temperature'
                    ? 'حرارة'
                    : type === 'humidity'
                    ? 'رطوبة'
                    : 'بطارية'
                  : type}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Severity */}
      <View style={styles.formField}>
        <Text style={[styles.label, { color: theme.colors.text }]}>
          {isArabic ? 'الخطورة' : 'Severity'}
        </Text>
        <View style={styles.segmentControl}>
          {(['info', 'warning', 'critical'] as const).map((severity) => (
            <TouchableOpacity
              key={severity}
              style={[
                styles.segment,
                rule.severity === severity && {
                  backgroundColor:
                    severity === 'critical'
                      ? theme.colors.error
                      : severity === 'warning'
                      ? theme.colors.warning
                      : theme.colors.info,
                },
                { borderColor: theme.colors.border },
              ]}
              onPress={() => updateRule(index, 'severity', severity)}
            >
              <Text
                style={[
                  styles.segmentText,
                  { color: rule.severity === severity ? '#fff' : theme.colors.text },
                ]}
              >
                {isArabic
                  ? severity === 'critical'
                    ? 'حرج'
                    : severity === 'warning'
                    ? 'تحذير'
                    : 'معلومة'
                  : severity}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Thresholds */}
      <View style={styles.formRow}>
        <View style={[styles.formField, { flex: 1, marginRight: 8 }]}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {isArabic ? 'الحد الأدنى' : 'Low Threshold'}
          </Text>
          <TextInput
            style={[
              styles.input,
              {
                backgroundColor: theme.colors.card,
                color: theme.colors.text,
                borderColor: theme.colors.border,
              },
            ]}
            value={rule.thresholdLow}
            onChangeText={(value) => updateRule(index, 'thresholdLow', value)}
            keyboardType="numeric"
            placeholder={isArabic ? 'مثال: 2' : 'e.g., 2'}
            placeholderTextColor={theme.colors.textSecondary}
          />
        </View>
        <View style={[styles.formField, { flex: 1 }]}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {isArabic ? 'الحد الأعلى' : 'High Threshold'}
          </Text>
          <TextInput
            style={[
              styles.input,
              {
                backgroundColor: theme.colors.card,
                color: theme.colors.text,
                borderColor: theme.colors.border,
              },
            ]}
            value={rule.thresholdHigh}
            onChangeText={(value) => updateRule(index, 'thresholdHigh', value)}
            keyboardType="numeric"
            placeholder={isArabic ? 'مثال: 8' : 'e.g., 8'}
            placeholderTextColor={theme.colors.textSecondary}
          />
        </View>
      </View>

      {/* Advanced Settings */}
      <View style={styles.formRow}>
        <View style={[styles.formField, { flex: 1, marginRight: 8 }]}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {isArabic ? 'التذبذب' : 'Hysteresis'}
          </Text>
          <TextInput
            style={[
              styles.input,
              {
                backgroundColor: theme.colors.card,
                color: theme.colors.text,
                borderColor: theme.colors.border,
              },
            ]}
            value={rule.hysteresis}
            onChangeText={(value) => updateRule(index, 'hysteresis', value)}
            keyboardType="numeric"
            placeholder="0.5"
            placeholderTextColor={theme.colors.textSecondary}
          />
        </View>
        <View style={[styles.formField, { flex: 1 }]}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {isArabic ? 'التأخير (ث)' : 'Debounce (s)'}
          </Text>
          <TextInput
            style={[
              styles.input,
              {
                backgroundColor: theme.colors.card,
                color: theme.colors.text,
                borderColor: theme.colors.border,
              },
            ]}
            value={rule.debounceSec}
            onChangeText={(value) => updateRule(index, 'debounceSec', value)}
            keyboardType="numeric"
            placeholder="60"
            placeholderTextColor={theme.colors.textSecondary}
          />
        </View>
      </View>

      <View style={styles.formField}>
        <Text style={[styles.label, { color: theme.colors.text }]}>
          {isArabic ? 'فترة التهدئة (ث)' : 'Cooldown (s)'}
        </Text>
        <TextInput
          style={[
            styles.input,
            {
              backgroundColor: theme.colors.card,
              color: theme.colors.text,
              borderColor: theme.colors.border,
            },
          ]}
          value={rule.cooldownSec}
          onChangeText={(value) => updateRule(index, 'cooldownSec', value)}
          keyboardType="numeric"
          placeholder="300"
          placeholderTextColor={theme.colors.textSecondary}
        />
      </View>
    </View>
  );

  const renderModal = (isEdit: boolean) => (
    <Modal
      visible={isEdit ? showEditModal : showCreateModal}
      animationType="slide"
      onRequestClose={() => (isEdit ? setShowEditModal(false) : setShowCreateModal(false))}
    >
      <SafeAreaView
        style={[styles.modalContainer, { backgroundColor: theme.colors.background }]}
        edges={['top', 'bottom']}
      >
        <View
          style={[
            styles.modalHeader,
            { backgroundColor: theme.colors.card, borderBottomColor: theme.colors.border },
          ]}
        >
          <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
            {isEdit
              ? isArabic
                ? 'تعديل القالب'
                : 'Edit Template'
              : isArabic
              ? 'إنشاء قالب جديد'
              : 'Create New Template'}
          </Text>
          <TouchableOpacity
            onPress={() => (isEdit ? setShowEditModal(false) : setShowCreateModal(false))}
          >
            <Ionicons name="close" size={28} color={theme.colors.text} />
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.modalContent}>
          {/* Name English */}
          <View style={styles.formField}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {isArabic ? 'الاسم (إنجليزي)' : 'Name (English)'} *
            </Text>
            <TextInput
              style={[
                styles.input,
                {
                  backgroundColor: theme.colors.card,
                  color: theme.colors.text,
                  borderColor: theme.colors.border,
                },
              ]}
              value={name}
              onChangeText={setName}
              placeholder={
                isArabic ? 'مثال: Custom Vaccine Storage' : 'e.g., Custom Vaccine Storage'
              }
              placeholderTextColor={theme.colors.textSecondary}
            />
          </View>

          {/* Name Arabic */}
          <View style={styles.formField}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {isArabic ? 'الاسم (عربي)' : 'Name (Arabic)'} *
            </Text>
            <TextInput
              style={[
                styles.input,
                {
                  backgroundColor: theme.colors.card,
                  color: theme.colors.text,
                  borderColor: theme.colors.border,
                },
              ]}
              value={nameAr}
              onChangeText={setNameAr}
              placeholder={isArabic ? 'مثال: تخزين لقاحات مخصص' : 'e.g., تخزين لقاحات مخصص'}
              placeholderTextColor={theme.colors.textSecondary}
            />
          </View>

          {/* Description English */}
          <View style={styles.formField}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {isArabic ? 'الوصف (إنجليزي)' : 'Description (English)'}
            </Text>
            <TextInput
              style={[
                styles.input,
                styles.textArea,
                {
                  backgroundColor: theme.colors.card,
                  color: theme.colors.text,
                  borderColor: theme.colors.border,
                },
              ]}
              value={description}
              onChangeText={setDescription}
              placeholder={isArabic ? 'وصف القالب...' : 'Template description...'}
              placeholderTextColor={theme.colors.textSecondary}
              multiline
              numberOfLines={3}
            />
          </View>

          {/* Description Arabic */}
          <View style={styles.formField}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {isArabic ? 'الوصف (عربي)' : 'Description (Arabic)'}
            </Text>
            <TextInput
              style={[
                styles.input,
                styles.textArea,
                {
                  backgroundColor: theme.colors.card,
                  color: theme.colors.text,
                  borderColor: theme.colors.border,
                },
              ]}
              value={descriptionAr}
              onChangeText={setDescriptionAr}
              placeholder={isArabic ? 'وصف القالب...' : 'Template description...'}
              placeholderTextColor={theme.colors.textSecondary}
              multiline
              numberOfLines={3}
            />
          </View>

          {/* Category */}
          <View style={styles.formField}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {isArabic ? 'التصنيف' : 'Category'}
            </Text>
            <View style={styles.segmentControl}>
              {(['pharmaceutical', 'medical', 'laboratory'] as const).map((cat) => (
                <TouchableOpacity
                  key={cat}
                  style={[
                    styles.segment,
                    category === cat && { backgroundColor: theme.colors.primary },
                    { borderColor: theme.colors.border },
                  ]}
                  onPress={() => setCategory(cat)}
                >
                  <Text
                    style={[
                      styles.segmentText,
                      { color: category === cat ? '#fff' : theme.colors.text },
                    ]}
                  >
                    {isArabic
                      ? cat === 'pharmaceutical'
                        ? 'صيدلانية'
                        : cat === 'medical'
                        ? 'طبية'
                        : 'مختبرية'
                      : cat}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Active Status */}
          <View style={[styles.formField, styles.switchRow]}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {isArabic ? 'نشط' : 'Active'}
            </Text>
            <Switch
              value={isActive}
              onValueChange={setIsActive}
              trackColor={{ true: theme.colors.primary }}
            />
          </View>

          {/* Rules */}
          <View style={styles.rulesSection}>
            <View style={styles.rulesSectionHeader}>
              <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
                {isArabic ? 'القواعد' : 'Rules'}
              </Text>
              <TouchableOpacity
                style={[styles.addRuleButton, { backgroundColor: theme.colors.primary }]}
                onPress={addRule}
              >
                <Ionicons name="add" size={20} color="#fff" />
                <Text style={styles.addRuleText}>{isArabic ? 'إضافة قاعدة' : 'Add Rule'}</Text>
              </TouchableOpacity>
            </View>

            {rules.map((rule, index) => renderRuleForm(rule, index))}

            {rules.length === 0 && (
              <View
                style={[
                  styles.emptyRules,
                  { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
                ]}
              >
                <Ionicons
                  name="alert-circle-outline"
                  size={48}
                  color={theme.colors.textSecondary}
                />
                <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
                  {isArabic
                    ? 'لا توجد قواعد. انقر "إضافة قاعدة" للبدء'
                    : 'No rules. Click "Add Rule" to start'}
                </Text>
              </View>
            )}
          </View>
        </ScrollView>

        {/* Save Button */}
        <View
          style={[
            styles.modalFooter,
            { backgroundColor: theme.colors.card, borderTopColor: theme.colors.border },
          ]}
        >
          <TouchableOpacity
            style={[styles.saveButton, { backgroundColor: theme.colors.primary }]}
            onPress={handleSave}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <>
                <Ionicons name="checkmark-circle" size={24} color="#fff" />
                <Text style={styles.saveButtonText}>
                  {isArabic ? 'حفظ القالب' : 'Save Template'}
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </Modal>
  );

  if (loading) {
    return (
      <View style={[styles.centered, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  if (loadError) {
    return (
      <View style={[styles.centered, { backgroundColor: theme.colors.background }]}>
        <Ionicons
          name="alert-circle-outline"
          size={48}
          color={theme.colors.error}
          style={{ marginBottom: 12 }}
        />
        <Text
          style={{ color: theme.colors.text, fontSize: 16, textAlign: 'center', marginBottom: 16 }}
        >
          {isArabic ? 'فشل تحميل القوالب' : 'Failed to load templates'}
        </Text>
        <TouchableOpacity
          onPress={loadTemplates}
          style={[styles.createButton, { backgroundColor: theme.colors.primary }]}
        >
          <Text style={styles.saveButtonText}>{isArabic ? 'إعادة المحاولة' : 'Retry'}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View
      style={[
        styles.container,
        { backgroundColor: theme.colors.background, paddingTop: headerHeight },
      ]}
    >
      {/* Header with info */}
      <View
        style={[
          styles.header,
          { backgroundColor: theme.colors.card, flexDirection: isRTL ? 'row-reverse' : 'row' },
        ]}
      >
        <View style={{ flex: 1 }}>
          <Text
            style={[
              styles.headerTitle,
              { color: theme.colors.primary, textAlign: isRTL ? 'right' : 'left' },
            ]}
          >
            {isArabic ? 'القوالب المخصصة' : 'Custom Templates'}
          </Text>
          <Text
            style={[
              styles.headerSubtitle,
              { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
            ]}
          >
            {isArabic ? 'قوالب التنبيهات الخاصة بمنظمتك' : 'Your organization alarm templates'}
          </Text>
        </View>
        <TouchableOpacity
          style={[styles.createButton, { backgroundColor: theme.colors.primary }]}
          onPress={openCreateModal}
        >
          <Ionicons name="add" size={24} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Templates List */}
      {templates.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Ionicons name="document-text-outline" size={64} color={theme.colors.textSecondary} />
          <Text style={[styles.emptyTitle, { color: theme.colors.text }]}>
            {isArabic ? 'لا توجد قوالب مخصصة' : 'No Custom Templates'}
          </Text>
          <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
            {isArabic
              ? 'انقر على + لإنشاء قالب تنبيهات مخصص'
              : 'Tap + to create a custom alarm template'}
          </Text>
        </View>
      ) : (
        <FlatList
          data={templates}
          keyExtractor={(item) => item.id}
          renderItem={renderTemplateItem}
          contentContainerStyle={styles.listContent}
        />
      )}

      {/* Modals */}
      {renderModal(false)}
      {renderModal(true)}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '700',
  },
  headerSubtitle: {
    fontSize: 14,
    marginTop: 4,
  },
  createButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContent: {
    padding: 16,
  },
  templateCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    marginBottom: 12,
  },
  templateHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  templateName: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4,
  },
  templateDescription: {
    fontSize: 14,
    marginBottom: 8,
  },
  templateMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  categoryBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  categoryText: {
    fontSize: 12,
    fontWeight: '600',
  },
  rulesCount: {
    fontSize: 12,
  },
  templateActions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    textAlign: 'center',
  },
  modalContainer: {
    flex: 1,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  modalContent: {
    flex: 1,
    padding: 16,
  },
  modalFooter: {
    padding: 16,
    borderTopWidth: 1,
  },
  formField: {
    marginBottom: 16,
  },
  formRow: {
    flexDirection: 'row',
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  segmentControl: {
    flexDirection: 'row',
    borderRadius: 8,
    overflow: 'hidden',
  },
  segment: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderWidth: 1,
  },
  segmentText: {
    fontSize: 14,
    fontWeight: '600',
  },
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  rulesSection: {
    marginTop: 8,
  },
  rulesSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  addRuleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    gap: 4,
  },
  addRuleText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  ruleCard: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  ruleHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  ruleTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  emptyRules: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: 12,
    padding: 32,
    alignItems: 'center',
  },
  saveButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    gap: 8,
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});
